<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Intune — Device Configurations"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      :showSearch="false"
      :showExport="false"
      @refresh="loadData"
    />

    <div class="page-body">
      <div v-if="error" class="error-banner">{{ error }}</div>
      <div v-if="tableState === 'loading'" class="loading-block">Loading device configurations...</div>
      <div v-else-if="items.length === 0" class="empty-block">No device configuration profiles found.</div>
      <div v-else class="tab-content">
        <table class="data-table">
          <thead><tr><th>Configuration</th><th>Platform</th><th>Created</th><th>Modified</th></tr></thead>
          <tbody>
            <tr v-for="c in items" :key="c.configId" class="cursor-pointer" @click="openDetail('Device Configuration', c)">
              <td class="cell-name">{{ c.displayName }}</td>
              <td>{{ c.platform || '--' }}</td>
              <td class="date-cell">{{ formatDate(c.createdDateTime) }}</td>
              <td class="date-cell">{{ formatDate(c.lastModifiedDateTime) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showDrawer" class="drawer-overlay" @click.self="closeDrawer">
      <div class="drawer">
        <div class="drawer-header">
          <div class="drawer-title-group">
            <span class="drawer-type-badge">{{ drawerType }}</span>
            <h2>{{ drawerItem.displayName }}</h2>
          </div>
          <button class="drawer-close" @click="closeDrawer">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="20" height="20">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="drawer-body">
          <div class="drawer-meta-grid">
            <div class="meta-item">
              <span class="meta-label">Platform</span>
              <span class="meta-value">{{ drawerItem.platform || '--' }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Created</span>
              <span class="meta-value">{{ formatDate(drawerItem.createdDateTime) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Modified</span>
              <span class="meta-value">{{ formatDate(drawerItem.lastModifiedDateTime) }}</span>
            </div>
          </div>
          <div class="drawer-section">
            <div class="section-label">Configuration JSON</div>
            <pre class="json-block">{{ JSON.stringify(drawerItem.raw, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import { getEntraIntune, getEntraTenantSummary, getEntraTenantCapabilities } from '@/services/entra-api.js';
import { getCapabilityStateLabel } from '@/services/entra-utils.js';

export default {
  name: 'EntraIntuneConfigs',
  components: { EntraPageHeader },
  data: function() { return { tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '', freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [], items: [], tableState: 'loading', error: '', showDrawer: false, drawerType: '', drawerItem: {} }; },
  methods: {
    formatDate: function(ts) { if (!ts) return '--'; try { return new Date(ts).toLocaleDateString(); } catch(e) { return '--'; } },
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) { this.error = 'Please select a tenant first.'; return; }
      getEntraTenantSummary(this.tenantId).then(function(data) {
        var t = data.tenant || data;
        self.tenantName = t.displayName || ''; self.primaryDomain = t.primaryDomain || ''; self.regionCode = t.regionCode || ''; self.tenantStatus = t.status || ''; self.lastCollection = t.lastSuccessfulCollectionAt || '';
      }).catch(function() {});
      getEntraTenantCapabilities(this.tenantId).then(function(data) {
        self.capabilities = (data || []).map(function(c) { return { key: c.capabilityKey, state: c.state, stateLabel: getCapabilityStateLabel(c.state) }; });
      }).catch(function() {});
      getEntraIntune(this.tenantId).then(function(d) { self.items = d.deviceConfigs || []; self.tableState = 'loaded'; }).catch(function(err) { self.error = err.message; });
    },
    openDetail: function(type, item) { this.drawerType = type; this.drawerItem = item; this.showDrawer = true; },
    closeDrawer: function() { this.showDrawer = false; this.drawerItem = {}; }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; background: #F0F9FF; }
.page-body { padding: 20px 24px; }
.error-banner { padding: 12px 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: #991b1b; font-size: 0.8125rem; margin-bottom: 16px; }
.loading-block { padding: 48px; text-align: center; color: #94a3b8; }
.empty-block { padding: 48px; text-align: center; color: #94a3b8; font-size: 0.875rem; }
.tab-content { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; text-align: left; padding: 10px 14px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 9px 14px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.data-table tbody tr:hover { background: #F0F9FF; }
.cell-name { font-weight: 600; color: #0C4A6E; }
.date-cell { font-size: 0.75rem; color: #64748b; white-space: nowrap; }
.cursor-pointer { cursor: pointer; }
.drawer-overlay { position: fixed; top: 0; right: 0; bottom: 0; left: 0; background: rgba(12,74,110,0.4); z-index: 100; display: flex; justify-content: flex-end; backdrop-filter: blur(2px); }
.drawer { width: 640px; max-width: 92vw; height: 100%; background: #ffffff; overflow-y: auto; box-shadow: -12px 0 40px rgba(3,105,161,0.12); animation: drawer-slide 0.22s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes drawer-slide { from { transform: translateX(40px); opacity: 0.7; } to { transform: translateX(0); opacity: 1; } }
.drawer-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 16px 20px; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; background: rgba(255,255,255,0.95); backdrop-filter: blur(8px); z-index: 1; }
.drawer-title-group { display: flex; flex-direction: column; gap: 6px; }
.drawer-type-badge { font-size: 0.625rem; font-weight: 700; color: #0369A1; background: #dbeafe; padding: 2px 8px; border-radius: 4px; align-self: flex-start; text-transform: uppercase; }
.drawer-header h2 { margin: 0; font-size: 1rem; font-weight: 600; color: #0C4A6E; word-break: break-word; }
.drawer-close { background: none; border: none; color: #94a3b8; cursor: pointer; padding: 6px; border-radius: 8px; }
.drawer-close:hover { color: #475569; background: #f1f5f9; }
.drawer-body { padding: 20px; }
.drawer-meta-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid #f1f5f9; }
.meta-item { display: flex; flex-direction: column; gap: 3px; }
.meta-label { font-size: 0.625rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
.meta-value { font-size: 0.8125rem; color: #0C4A6E; }
.drawer-section { margin-bottom: 16px; }
.section-label { font-size: 0.75rem; font-weight: 700; color: #0369A1; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 8px; }
.json-block { background: #F0F9FF; border: 1px solid #bae6fd; border-radius: 8px; padding: 14px; font-size: 0.6875rem; color: #0C4A6E; overflow-x: auto; white-space: pre-wrap; font-family: 'SF Mono', 'Fira Code', monospace; line-height: 1.5; }
</style>
